import { FunctionComponent, ReactNode, useLayoutEffect, useState, useTransition } from 'react';
import { Router as BaseRouter, UNSAFE_createBrowserHistory, type Navigator } from 'react-router-dom';
import { PendingNavigationContext } from './pendingNavigation';
import { NavigationProgress } from './NavigationProgress';
import { createBlockingNavigation } from './navigationBlocker';

type BrowserHistory = ReturnType<typeof UNSAFE_createBrowserHistory>;

/**
 * Lo mismo que `<BrowserRouter>` (misma history, mismo `startTransition` al
 * navegar) pero con `useTransition`, que es lo único que nos deja SABER que hay
 * una navegación pendiente. `BrowserRouter` usa `React.startTransition` suelto:
 * cuando el chunk de la ruta destino no estaba, la pantalla vieja quedaba
 * congelada ~500 ms sin ninguna señal y el rail seguía marcando la anterior.
 *
 * Cubre toda navegación (rail, menú mobile, links de las pantallas, `navigate()`,
 * atrás/adelante), no solo los clics del rail.
 */
export const TransitionBrowserRouter: FunctionComponent<{ children?: ReactNode }> = ({ children }) => {
  const [history] = useState<BrowserHistory>(() => UNSAFE_createBrowserHistory({ window, v5Compat: true }));
  // El navigator que ve el router es la misma history con push/replace pasando por los
  // bloqueadores registrados (`navigationBlocker.ts`, p. ej. un editor con cambios sin guardar).
  // Sin bloqueadores llaman directo a la history, igual que antes.
  const [blocking] = useState(() => createBlockingNavigation(history));
  const [navigator] = useState<Navigator>(() => ({
    createHref: (to) => history.createHref(to),
    encodeLocation: (to) => history.encodeLocation(to),
    go: (delta) => history.go(delta),
    push: (to, state) => blocking.push(to, state),
    replace: (to, state) => blocking.replace(to, state)
  }));
  const [state, setState] = useState({ action: history.action, location: history.location });
  const [target, setTarget] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  useLayoutEffect(
    () =>
      history.listen((update) => {
        // Atrás/adelante del navegador con un bloqueador activo: la URL se restaura y no se
        // pinta la ruta nueva salvo que confirmen (ver `filterPop`).
        if (update.action === 'POP' && !blocking.filterPop(update)) return;
        // El destino se publica urgente (el rail lo marca en el mismo frame del clic);
        // la ruta nueva entra en transición, como en BrowserRouter.
        setTarget(update.location.pathname);
        startTransition(() => setState(update));
      }),
    [history, blocking]
  );

  const pendingPathname = isPending ? target : null;

  return (
    <PendingNavigationContext value={pendingPathname}>
      <BaseRouter location={state.location} navigationType={state.action} navigator={navigator}>
        {children}
      </BaseRouter>
      <NavigationProgress active={pendingPathname !== null} />
    </PendingNavigationContext>
  );
};
